// Code-point-safe truncation.
//
// `String.prototype.slice` operates on UTF-16 code units. For BMP characters
// (the vast majority of Kanji, CJK Unified, Hiragana, Katakana) that's
// equivalent to slicing by characters. But astral characters (CJK Extension B,
// most emoji) are represented as surrogate pairs in JS strings — two code
// units that *must* be kept together. A naive `s.slice(0, n)` can cut between
// the high and low surrogate, producing an invalid UTF-16 string. Downstream
// (Faro JSON encode, OTel SDK, JSON.stringify, network transport) all assume
// valid Unicode and will either drop the field, throw, or corrupt it.
//
// `Array.from(s)` iterates the string as code points (handling surrogate
// pairs as single elements), so re-joining a prefix is always valid Unicode.
//
// `maxCodePoints` budgets code points, not code units; for the use case
// (Faro event/session attribute size limits) this is conservative since one
// code point can occupy 1-4 UTF-8 bytes.
export function truncateCodePoints(s: string, maxCodePoints: number): string {
  if (s == null) return s as any;
  if (typeof s !== 'string') return s;
  // Fast path: ASCII-only strings have no surrogates and length == code points.
  // Avoid the Array.from allocation when it cannot help.
  if (s.length <= maxCodePoints) return s;

  let count = 0;
  let result = '';
  // Iterating a string yields code points (not units), so a single emoji or
  // astral CJK char advances `count` by 1 and appends both surrogates together.
  for (const ch of s) {
    if (count >= maxCodePoints) break;
    result += ch;
    count++;
  }
  return result;
}
